import React, { FC, useEffect, useState } from 'react';

import {
  CheckType,
  NativeResources,
  ResourceTagCondition,
} from '@ariksa/inventory-core/api';
import { Check, Option } from '@ariksa/inventory-core/dist/api';
import { Box, Wrap, Stack, WrapItem, HStack } from '@chakra-ui/react';
import { map, find, findIndex } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { Select } from 'components/DataEntry';
import {
  containOptions,
  ec2OsTechOptions,
  ec2WithVersionOrVulnerabilityOptions,
  severityOptions,
  vulnerabilityCompareOptions,
} from 'containers/Inventory/AllowList/Components/AllowListWizard/Steps/AllowListCriteria/ResourceTypeCriteria/utils';
import { selectAllowList } from 'containers/Inventory/AllowList/selector';

import { useAllowListContext } from '../../../../../context';
import { actions } from '../../../../../slice';
import { emptyEC2Criterion, getEC2CriterionDefaults } from '../utils';

interface Props {
  id: string;
  osOptions: Record<string, any>[];
  technologyOptions: Record<string, any>[];
}

export const ResourceTypeComponent: FC<Props> = props => {
  const { id, osOptions, technologyOptions } = props;
  const { resourceTypeCriteria, ec2Applications } = useSelector(
    selectAllowList,
  );
  // Next-disabled and the EC2 flag are derived in AllowListCriteria from all
  // rows, so this row only needs the resource type options.
  const { resourceTypeOptions } = useAllowListContext();
  const item = resourceTypeCriteria[id];
  const [checkType, setCheckType] = useState<Record<string, any>>({});
  const [selectedResourceType, setSelectedResourceType] = useState<
    Record<string, any>
  >({});
  const [selectedOSTechnology, setSelectedOSTechnology] = useState<
    Record<string, any>
  >({});
  const [compare, setCompare] = useState<Record<string, any>>(
    vulnerabilityCompareOptions[0],
  );
  const [severity, setSeverity] = useState<Record<string, any>>(
    severityOptions[0],
  );

  const [isOSSelected, setIsOSSelected] = useState(false);
  const [isVersionSelected, setIsVersionSelected] = useState(true);
  const [version, setVersion] = useState<Record<string, any>>({});
  const [versionOptions, setVersionOptions] = useState<Record<string, any>[]>(
    [],
  );
  const dispatch = useDispatch();

  // Removed the unused useReducer copy of this row; `item` is read directly.

  // Persist the OS + version defaults the dropdowns show for an EC2 row saved
  // without an OS (added before applications loaded), so the saved criterion
  // matches the UI and the step's validity check.
  useEffect(() => {
    if (
      item?.resource_type === NativeResources.Ec2Instance &&
      !item?.value_1 &&
      item?.key !== Option.Technology &&
      !!osOptions.length
    )
      dispatch(
        actions.updateResourceTypeCriteria({
          id,
          value: { ...item, ...getEC2CriterionDefaults(osOptions) },
        }),
      );
  }, [id, item, osOptions, dispatch]);

  //set check type
  useEffect(() => {
    setCheckType(
      item?.check_type === CheckType.NotContains
        ? containOptions[1]
        : containOptions[0],
    );

    // Default to OS + version when unset, like the dropdowns' defaultValue.
    setIsVersionSelected(!item?.check || item?.check === Check.Version);
    setIsOSSelected(!item?.key || item?.key === Option.Os);

    setCompare(
      find(vulnerabilityCompareOptions, o => o?.value === item?.comparison) ??
        vulnerabilityCompareOptions[0],
    );
  }, [item]);

  //set resource type
  useEffect(() => {
    setSelectedResourceType(
      find(resourceTypeOptions, o => item?.resource_type === o?.value) ||
        resourceTypeOptions[0],
    );
  }, [item, resourceTypeOptions]);

  //set selected os or technology
  useEffect(() => {
    const options = isOSSelected ? osOptions : technologyOptions;
    setSelectedOSTechnology(
      find(options, o => item?.value_1 === o?.value) || options[0],
    );
  }, [item, osOptions, technologyOptions, isOSSelected]);

  useEffect(() => {
    if (isVersionSelected) {
      setVersionOptions(
        map(selectedOSTechnology?.data, o => ({
          label: (
            <HStack spacing={1}>
              <Box>{o.version}</Box>
              {o.eol && <Box fontSize={10}>(E.O.L. {o.eol})</Box>}
            </HStack>
          ),
          value: o.version,
        })),
      );
    }
  }, [isVersionSelected, selectedOSTechnology]);

  //set severity
  useEffect(() => {
    if (isVersionSelected) setSeverity({});
    else {
      const severityOption = findIndex(
        severityOptions,
        o => o?.value === item?.value_2,
      );
      const _severity = severityOptions?.[severityOption] ?? severityOptions[0];
      setSeverity(_severity);
    }
  }, [item, isVersionSelected]);

  //set version
  useEffect(() => {
    if (isVersionSelected) {
      const index = findIndex(versionOptions, o => o?.value === item?.value_2);
      const _version = versionOptions?.[index] ?? versionOptions[0];
      setVersion(_version);
    } else {
      setVersion({});
    }
  }, [item, isVersionSelected, versionOptions]);

  // EC2 fields now follow the NEW resource type: switching to EC2 fills the
  // OS + version defaults and switching away clears them (they used to be
  // kept). The never-used key_condition branch was removed.
  const updateResourceType = resourceType => {
    dispatch(
      actions.updateResourceTypeCriteria({
        id,
        value: {
          resource_type: resourceType,
          key_condition: ResourceTagCondition.HasResourceType,
          ...(resourceType === NativeResources.Ec2Instance
            ? getEC2CriterionDefaults(osOptions)
            : emptyEC2Criterion),
        },
      }),
    );
  };

  const updateCriteriaField = (field, value) => {
    dispatch(actions.updateResourceTypeCriteriaField({ id, field, value }));
  };

  return (
    <Stack w="full">
      <Wrap>
        <WrapItem>
          <Box whiteSpace="nowrap" pt={1}>
            If an entity has resource type
          </Box>
        </WrapItem>
        <WrapItem>
          <Box w={64}>
            <Select
              options={resourceTypeOptions}
              value={selectedResourceType}
              onChange={selected => {
                //setSelectedResourceType(selected);
                updateResourceType(selected?.value);
              }}
            />
          </Box>
        </WrapItem>
        {selectedResourceType.value === NativeResources.Ec2Instance && (
          <>
            <WrapItem>
              <Box w="170px">
                <Select
                  options={containOptions}
                  defaultValue={containOptions[0]}
                  value={checkType}
                  onChange={selected => {
                    //setCheckType(selected);
                    updateCriteriaField('check_type', selected?.value);
                  }}
                />
              </Box>
            </WrapItem>
            <WrapItem>
              <Box w="180px">
                <Select
                  options={ec2OsTechOptions}
                  defaultValue={ec2OsTechOptions[0]}
                  value={
                    isOSSelected ? ec2OsTechOptions[0] : ec2OsTechOptions[1]
                  }
                  onChange={selected => {
                    if (selected?.value === Option.Os) {
                      updateCriteriaField('value_1', osOptions?.[0]?.value);
                      if (isVersionSelected)
                        updateCriteriaField(
                          'value_2',
                          osOptions?.[0]?.data?.[0]?.version,
                        );
                    } else {
                      updateCriteriaField(
                        'value_1',
                        technologyOptions?.[0]?.value,
                      );
                      if (isVersionSelected)
                        updateCriteriaField(
                          'value_2',
                          technologyOptions?.[0]?.data?.[0]?.version,
                        );
                    }
                    updateCriteriaField('key', selected?.value);
                  }}
                />
              </Box>
            </WrapItem>
            <WrapItem>
              <Box whiteSpace="nowrap" pt={1}>
                that is
              </Box>
            </WrapItem>
            <WrapItem>
              <Box w="175px">
                <Select
                  options={isOSSelected ? osOptions : technologyOptions}
                  value={selectedOSTechnology}
                  onChange={selected => {
                    updateCriteriaField('value_1', selected?.value);
                  }}
                  isLoading={ec2Applications.isLoading}
                  isDisabled={ec2Applications.isLoading}
                />
              </Box>
            </WrapItem>
            <WrapItem>
              <Box w={48}>
                <Select
                  options={ec2WithVersionOrVulnerabilityOptions}
                  value={
                    isVersionSelected
                      ? ec2WithVersionOrVulnerabilityOptions[0]
                      : ec2WithVersionOrVulnerabilityOptions[1]
                  }
                  onChange={selected => {
                    if (selected?.value === Check.Version)
                      updateCriteriaField(
                        'value_2',
                        selectedOSTechnology?.data?.[0]?.version,
                      );
                    else
                      updateCriteriaField('value_2', severityOptions[0]?.value);
                    updateCriteriaField('check', selected?.value);
                  }}
                />
              </Box>
            </WrapItem>
            <WrapItem>
              <HStack>
                {isVersionSelected && (
                  <>
                    <Box w="140px">
                      <Select
                        options={vulnerabilityCompareOptions}
                        value={compare}
                        onChange={selected => {
                          //setCompare(selected);
                          updateCriteriaField('comparison', selected?.value);
                        }}
                      />
                    </Box>
                    <Box w="230px">
                      <Select
                        options={versionOptions}
                        value={version}
                        onChange={selected => {
                          //setCompare(selected);
                          updateCriteriaField('value_2', selected?.value);
                        }}
                        isLoading={ec2Applications.isLoading}
                        isDisabled={ec2Applications.isLoading}
                      />
                    </Box>
                  </>
                )}
                {!isVersionSelected && (
                  <>
                    <Box whiteSpace="nowrap" pt={1}>
                      that has severity
                    </Box>
                    <Box w="150px">
                      <Select
                        options={vulnerabilityCompareOptions}
                        value={compare}
                        onChange={selected => {
                          //setCompare(selected);
                          updateCriteriaField('comparison', selected?.value);
                        }}
                      />
                    </Box>
                    <Box w={28}>
                      <Select
                        options={severityOptions}
                        value={severity}
                        onChange={selected => {
                          //setSeverity(selected);
                          updateCriteriaField('value_2', selected?.value);
                        }}
                      />
                    </Box>
                  </>
                )}
              </HStack>
            </WrapItem>
          </>
        )}
      </Wrap>
    </Stack>
  );
};
